import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, InputLabel, MenuItem, Pagination, Paper, Select, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
    TextField, Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import AdminJigsawService from "./AdminJigsawService";
import AdminJigsawPackService from "./AdminJigsawPackService";
import { readPackTag, writePackTag } from "./jigsawPackTagStorage";

const PRODUCT_BY_TIER = {
    free: "com.pzl.jigsaw.muse.pack.tier.free",
    t1: "com.pzl.jigsaw.muse.pack.tier.1",
    t2: "com.pzl.jigsaw.muse.pack.tier.2",
};

const PACK_STATUSES = [
    { id: "DRAFT", label: "Draft" },
    { id: "READY", label: "Ready" },
    { id: "PUBLISHED", label: "Published" },
];

const STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: "DRAFT", label: "Draft" },
    { id: "PUBLISHED", label: "Published" },
];

const SORT_COLUMNS = [
    { id: "title", label: "Pack" },
    { id: "productId", label: "Product" },
    { id: "tier", label: "Tier" },
    { id: "pictureCount", label: "Pictures" },
    { id: "status", label: "Status" },
    { id: "createdAt", label: "Created at" },
    { id: "publishedAt", label: "Published at" },
];

const STATUS_RANK = { DRAFT: 0, READY: 1, PUBLISHED: 2 };
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

function packStatusOf(row) {
    const value = String(row?.status || "").trim().toUpperCase();
    if (PACK_STATUSES.some((item) => item.id === value)) return value;
    return row?.published === true ? "PUBLISHED" : "DRAFT";
}

function statusChipColor(status) {
    if (status === "PUBLISHED") return "success";
    if (status === "READY") return "info";
    return "default";
}

function tagLabel(tag) {
    const value = String(tag || "").trim();
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function sortTagsArtFirst(rows) {
    return [...rows].sort((a, b) => {
        const aArt = String(a.tag).toLowerCase() === "art" ? 0 : 1;
        const bArt = String(b.tag).toLowerCase() === "art" ? 0 : 1;
        if (aArt !== bArt) return aArt - bArt;
        return String(a.tag).localeCompare(String(b.tag), undefined, { sensitivity: "base" });
    });
}

function formatDateTime(value) {
    if (value == null || value === "") return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isFinite(d.getTime())) return "—";
    return `${d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

function packDetailPath(pack) {
    const id = pack?.id;
    if (!id) return "/admin/jigsaw/packs";
    return `/admin/jigsaw/packs/${encodeURIComponent(id)}`;
}

function emptyForm() {
    return {
        title: "",
        tag: "art",
    };
}

function packSortValue(pack, field) {
    if (field === "title") return String(pack.title || "").toLowerCase();
    if (field === "productId") return String(pack.productId || "").toLowerCase();
    if (field === "tier") return String(pack.tier || "").toLowerCase();
    if (field === "pictureCount") return Number(pack.pictureCount) || 0;
    if (field === "status") return STATUS_RANK[packStatusOf(pack)] ?? 0;
    if (field === "createdAt") {
        const created = pack.createdAt ? new Date(pack.createdAt).getTime() : 0;
        return Number.isFinite(created) ? created : 0;
    }
    const time = pack.publishedAt ? new Date(pack.publishedAt).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
}

function comparePacks(a, b, field, direction) {
    const sign = direction === "asc" ? 1 : -1;
    const left = packSortValue(a, field);
    const right = packSortValue(b, field);
    if (left < right) return -1 * sign;
    if (left > right) return 1 * sign;
    return String(a.title || "").localeCompare(String(b.title || ""), undefined, { sensitivity: "base" });
}

export default function AdminJigsawPackPage() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortField, setSortField] = useState("publishedAt");
    const [sortDirection, setSortDirection] = useState("desc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const load = useCallback(async () => {
        setLoading(true);
        const result = await AdminJigsawPackService.list();
        setLoading(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load packs" });
            return;
        }
        setRows(result.data || []);
    }, []);

    useEffect(() => { load(); }, [load]);

    const visibleRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows
            .filter((pack) => {
                if (statusFilter !== "all" && packStatusOf(pack) !== statusFilter) return false;
                if (!query) return true;
                const haystack = [pack.title, pack.productId, pack.tier].join(" ").toLowerCase();
                return haystack.includes(query);
            })
            .sort((a, b) => comparePacks(a, b, sortField, sortDirection));
    }, [rows, search, statusFilter, sortField, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
    const pageRows = visibleRows.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, sortField, sortDirection, pageSize]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortField(field);
        setSortDirection(field === "publishedAt" || field === "createdAt" ? "desc" : "asc");
    };

    const openEditor = async () => {
        setForm(emptyForm());
        setEditorOpen(true);
        setTagsLoading(true);
        const result = await AdminJigsawService.getDistinctTags("PUBLISHED", "english");
        setTagsLoading(false);
        const rows = result.success ? sortTagsArtFirst(result.data || []) : [];
        setTags(rows);
        const art = rows.find((row) => String(row.tag).toLowerCase() === "art");
        const nextTag = art?.tag || rows[0]?.tag || "art";
        setForm((prev) => ({ ...prev, tag: nextTag }));
    };

    const save = async () => {
        const tag = form.tag.trim();
        const title = form.title.trim();
        if (!tag || !title) {
            setSnackbar({ open: true, severity: "warning", message: "Choose a tag and a temporary name" });
            return;
        }
        const body = {
            title,
            productId: PRODUCT_BY_TIER.t2,
            tier: "t2",
            sortOrder: 0,
            published: false,
        };
        setSaving(true);
        const result = await AdminJigsawPackService.create(body);
        setSaving(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Save failed" });
            return;
        }
        setEditorOpen(false);
        const createdId = result.data?.id;
        if (createdId) writePackTag(createdId, form.tag.trim());
        navigate(packDetailPath(result.data), { state: { tag: form.tag.trim() } });
    };

    const confirmDelete = (pack) => {
        setDeleteTarget(pack);
        setConfirmOpen(true);
    };

    const deletePack = async (id) => {
        const result = await AdminJigsawPackService.remove(id);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Delete failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Pack deleted" });
        load();
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h5">Jigsaw packs</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Start with a tag and a temporary name. On the next page, pick similar pictures in that tag (8 for T1, 12 for T2), then publish.
                    </Typography>
                </Box>
                <Button variant="contained" onClick={openEditor}>
                    New pack
                </Button>
            </Stack>

            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, product, or tier"
                    sx={{ minWidth: 240 }}
                />
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 160 }}
                >
                    {STATUS_FILTERS.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Sort"
                    value={sortField}
                    onChange={(e) => {
                        const next = e.target.value;
                        setSortField(next);
                        setSortDirection(next === "publishedAt" || next === "createdAt" ? "desc" : "asc");
                    }}
                    sx={{ minWidth: 180 }}
                >
                    {SORT_COLUMNS.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                    ))}
                </TextField>
            </Stack>

            <Paper>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {SORT_COLUMNS.map((column) => (
                                    <TableCell key={column.id}>
                                        <TableSortLabel
                                            active={sortField === column.id}
                                            direction={sortField === column.id ? sortDirection : "asc"}
                                            onClick={() => toggleSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : visibleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        {rows.length === 0 ? "No packs yet." : "No packs match."}
                                    </TableCell>
                                </TableRow>
                            ) : pageRows.map((pack) => {
                                const status = packStatusOf(pack);
                                const statusLabel = PACK_STATUSES.find((item) => item.id === status)?.label || status;
                                return (
                                <TableRow key={pack.id} hover>
                                    <TableCell>
                                        <Typography
                                            component={RouterLink}
                                            to={packDetailPath(pack)}
                                            state={readPackTag(pack.id) ? { tag: readPackTag(pack.id) } : undefined}
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: "inherit", textDecoration: "none", display: "block" }}
                                        >
                                            {pack.title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                                            {pack.productId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {(pack.tier || "").toUpperCase()}
                                        {pack.priceUsd == null ? "" : ` $${pack.priceUsd}`}
                                    </TableCell>
                                    <TableCell>{pack.pictureCount ?? 0}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel}
                                            color={statusChipColor(status)}
                                            variant={status === "DRAFT" ? "outlined" : "filled"}
                                        />
                                    </TableCell>
                                    <TableCell>{formatDateTime(pack.createdAt)}</TableCell>
                                    <TableCell>{status === "PUBLISHED" ? formatDateTime(pack.publishedAt) : "—"}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                const tag = readPackTag(pack.id);
                                                navigate(packDetailPath(pack), tag ? { state: { tag } } : undefined);
                                            }}
                                        >
                                            Open
                                        </Button>
                                        <IconButton aria-label={`Delete ${pack.title}`} onClick={() => confirmDelete(pack)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            {!loading && visibleRows.length > 0 ? (
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                >
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="jigsaw-pack-page-size-label">Page size</InputLabel>
                        <Select
                            labelId="jigsaw-pack-page-size-label"
                            label="Page size"
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Pagination
                        count={totalPages}
                        page={Math.min(page, totalPages)}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Stack>
            ) : null}

            <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New pack</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Tag"
                            value={form.tag}
                            onChange={(e) => setForm((prev) => ({ ...prev, tag: e.target.value }))}
                            disabled={tagsLoading && tags.length === 0}
                            helperText={tagsLoading ? "Loading tags" : "Art is first. The detail page searches this tag."}
                        >
                            {tags.length === 0 ? (
                                <MenuItem value={form.tag || "art"}>{tagLabel(form.tag || "art")}</MenuItem>
                            ) : tags.map((row) => (
                                <MenuItem key={row.tag} value={row.tag}>
                                    {tagLabel(row.tag)}{row.count == null ? "" : ` (${row.count})`}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Temporary name"
                            value={form.title}
                            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder="Art pack"
                            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving || !form.title.trim() || !form.tag.trim()}
                    >
                        {saving ? "Saving" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Delete pack"
                message="Delete this pack? Pictures return to the jigsaw list. Packs that already have purchases cannot be deleted."
                targetName={deleteTarget?.title}
                targetId={deleteTarget?.id}
                callback={deletePack}
            />
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(next) => setSnackbar((prev) => ({ ...prev, open: next }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
